import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import ErrorMessage from '@components/Common/ErrorMessage';
import RecordLink from '@components/Common/RecordLink';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { formatDate, formatDateTime } from '@helpers/date';
import { humanizeEnum } from '@helpers/labels';
import { lastListView } from '@helpers/listView';
import { fmtMoney } from '@helpers/money';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PlagiarismOutlinedIcon from '@mui/icons-material/PlagiarismOutlined';
import { Box, Button } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { payoutLine, referredStatusLine } from './review';
import {
  gateRows,
  loadReferredPerson,
  pendingReasonLabel,
  personStage,
  referralMilestones,
  type ReferredPersonRecord,
} from './referredPerson';
import {
  flagLines,
  isScannedCv,
  referrerLabel,
  referrerPath,
  REWARD_STATUS_LABELS,
  VERDICT_TONE,
  verdictLabel,
  type RewardRow,
} from './review';
import './index.scss';

/**
 * One referred job seeker, as an entity workspace (owner, staging review: "Table can't be a
 * substitute as we can't accommodate all the data here").
 *
 * Read-only, as the table row was. Who they are and who referred them, every qualification gate as
 * the backend last evaluated it, the reward their qualification created and where it stands, and
 * the evidence the reviewer saw. Approving, rejecting and paying stay on the Payouts queue, which
 * is where a reviewer works through them in bulk.
 *
 * The record is composed from existing Super Admin queries; see referredPerson.ts for how and why.
 */

const TABS: WorkspaceTab[] = [{ id: 'overview', label: 'Overview', icon: <InfoOutlinedIcon /> }];

const initialsOf = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : (words[0] ?? '').slice(0, 2)).toUpperCase() || 'R';
};

const gateChip = (passed: boolean | null) =>
  passed === null ? (
    <CRMStatusChip status="inactive" label="Not recorded" />
  ) : (
    <CRMStatusChip status={passed ? 'success' : 'failed'} label={passed ? 'Passed' : 'Failed'} />
  );

const who = (at: string | null | undefined, by: string | null | undefined) =>
  at ? `${formatDateTime(at)}${by ? `, ${by}` : ''}` : null;

const ReferralWorkspace: React.FC = () => {
  const { jobSeekerId = '' } = useParams<{ jobSeekerId: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<ReferredPersonRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activityOpen, toggleActivity] = useActivityRail();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await loadReferredPerson(jobSeekerId);
      setRecord(r);
      if (!r.seeker) setError('This job seeker could not be found.');
      else if (!r.person) setError('This job seeker did not sign up through a referral link.');
    } catch {
      setError('Failed to load the referral. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [jobSeekerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !record) return <SkeletonLoader variant="workspace" />;
  const person = record?.person;
  if (error || !record || !person) {
    return <ErrorMessage page message={error ?? 'This referral could not be found.'} onRetry={load} />;
  }

  const rewards = record.rewards;
  const reward: RewardRow | undefined = rewards[0];
  const stage = personStage(person, reward);
  const name = person.name || record.seeker?.name || person.email || person.jobSeekerId;
  const referrerName = person.referrerName || person.referrerEmail || person.referrerId;
  const referrerKind = referrerLabel(person.referrerType);
  const gates = gateRows(person.checks);
  const recorded = gates.filter((g) => g.passed !== null);
  const passed = recorded.filter((g) => g.passed).length;
  const seekerPath = `/jobseekers/${person.jobSeekerId}`;

  const glance: GlanceRow[] = [
    { key: 'Referred by', value: `${referrerName} (${referrerKind})` },
    { key: 'Code', value: person.code || '-', mono: true },
    { key: 'Joined', value: formatDate(person.joinedAt), mono: true },
    { key: 'Qualified', value: person.qualifiedAt ? formatDate(person.qualifiedAt) : 'Not yet', mono: !!person.qualifiedAt },
    { key: 'Reward', value: reward ? `${fmtMoney(reward.amount, reward.currency)}, ${REWARD_STATUS_LABELS[reward.status] ?? humanizeEnum(reward.status)}` : 'None yet' },
  ];

  const identityRail = (
    <IdentityRail
      initials={initialsOf(name)}
      name={name}
      subId={person.jobSeekerId}
      statusChip={<CRMStatusChip status={stage.tone} label={stage.label} />}
      primaryAction={
        <Button variant="outlined" startIcon={<OpenInNewOutlinedIcon />} onClick={() => navigate(seekerPath)}>
          Open job seeker
        </Button>
      }
      glance={glance}
    />
  );

  const referralCard = (
    <SectionCard title="Referral" icon={<GroupAddOutlinedIcon />}>
      <WorkspaceField label="Job seeker" value={<RecordLink to={seekerPath}>{name}</RecordLink>} />
      <WorkspaceField label="Email" value={person.email || record.seeker?.email} />
      <WorkspaceField label="Signed up" value={formatDateTime(person.joinedAt)} />
      <WorkspaceField label="Code used" mono value={person.code} />
      <WorkspaceField label="Referred by" value={<RecordLink to={referrerPath(person)}>{referrerName}</RecordLink>} />
      <WorkspaceField label="Referrer type" value={referrerKind} />
      <WorkspaceField label="Referrer email" value={person.referrerEmail} />
    </SectionCard>
  );

  const qualificationCard = (
    <SectionCard
      title="Qualification"
      icon={<FactCheckOutlinedIcon />}
      meta={recorded.length ? `${passed} of ${recorded.length} checks passed` : undefined}
    >
      <WorkspaceField label="Status" full value={referredStatusLine(person)} />
      <WorkspaceField label="Waiting on" value={person.qualifiedAt ? 'Nothing, qualified' : pendingReasonLabel(person.pendingReason)} />
      <WorkspaceField label="Qualified" value={person.qualifiedAt ? formatDateTime(person.qualifiedAt) : 'Not yet'} />
      <WorkspaceField
        label="Profile completeness"
        mono
        value={typeof reward?.completenessAtQualification === 'number' ? `${reward.completenessAtQualification}% when it qualified` : null}
      />
      {gates.map((g) => (
        <WorkspaceField key={g.name} label={g.label} value={gateChip(g.passed)} />
      ))}
    </SectionCard>
  );

  const rewardCards = rewards.length ? (
    rewards.map((r) => (
      <SectionCard key={r.id} title="Reward" icon={<PaymentsOutlinedIcon />} meta={REWARD_STATUS_LABELS[r.status] ?? humanizeEnum(r.status)}>
        <WorkspaceField label="Amount" mono value={fmtMoney(r.amount, r.currency)} />
        <WorkspaceField
          label="Status"
          value={<CRMStatusChip status={personStage(person, r).tone} label={REWARD_STATUS_LABELS[r.status] ?? humanizeEnum(r.status)} />}
        />
        <WorkspaceField label="Created" value={formatDateTime(r.earnedAt)} />
        <WorkspaceField label="Reviewed" value={who(r.reviewedAt, r.reviewedBy)} />
        <WorkspaceField label="Rejection reason" full value={r.rejectedReason} />
        <WorkspaceField label="Paid" value={who(r.paidAt, r.paidBy)} />
        <WorkspaceField label="Payment reference" mono value={r.paymentRef} />
        <WorkspaceField label="Pay to" value={<RecordLink to={referrerPath(r)}>{r.referrerName || r.referrerEmail || r.referrerId}</RecordLink>} />
        <WorkspaceField label="Payout details" value={payoutLine(r) ?? 'No payout details given'} />
        <WorkspaceField label="Reward ID" mono value={r.id} />
      </SectionCard>
    ))
  ) : (
    <SectionCard title="Reward" icon={<PaymentsOutlinedIcon />}>
      <Box className="rf-ws-wide">
        <EmptyState
          compact
          title={record.rewardsComplete ? 'No reward yet' : 'Reward not found in the latest rewards'}
          body={
            record.rewardsComplete
              ? 'A reward is created when they qualify, and is reviewed before it can be paid.'
              : 'Only the most recent rewards were searched. Look this person up on the Payouts table.'
          }
        />
      </Box>
    </SectionCard>
  );

  const evidence = reward ? (() => {
    const rel = reward.relevance;
    const verdict = rel?.verdict ?? '';
    const flags = flagLines(reward.flags);
    const atQualification = gateRows(reward.checks).filter((g) => g.passed !== null);
    return (
      <SectionCard title="Evidence when it qualified" icon={<PlagiarismOutlinedIcon />}>
        <WorkspaceField
          label="Industry fit"
          value={(
            <span className="rf-chips">
              <CRMStatusChip status={VERDICT_TONE[verdict] ?? 'pending'} label={verdictLabel(verdict)} />
              {isScannedCv(reward.checks, rel) && <CRMStatusChip status="needsreview" label="Scanned CV: check by eye" />}
            </span>
          )}
        />
        <WorkspaceField label="Score" mono value={typeof rel?.score === 'number' ? String(rel.score) : null} />
        <WorkspaceField label="CV text" mono value={`${(rel?.cvTextChars ?? reward.resumeChars ?? 0).toLocaleString('en-IN')} characters`} />
        <WorkspaceField label="Industries" value={(rel?.industries ?? []).map((i) => humanizeEnum(i)).join(', ')} />
        <WorkspaceField label="Matched terms" full value={(rel?.matchedTerms ?? []).join(', ') || 'None'} />
        <WorkspaceField label="Other fields" full value={(rel?.negativeTerms ?? []).join(', ')} />
        <WorkspaceField
          label="Flags"
          full
          value={flags.length ? (
            <span className="rf-chips">
              {flags.map((f) => <CRMStatusChip key={f.text} status={f.warn ? 'needsreview' : 'inactive'} label={f.text} />)}
            </span>
          ) : 'Nothing unusual'}
        />
        <WorkspaceField
          label="Checks then"
          full
          value={atQualification.length ? (
            <span className="rf-chips">
              {atQualification.map((g) => (
                <CRMStatusChip key={g.name} status={g.passed ? 'success' : 'failed'} label={`${g.passed ? 'Yes' : 'No'}: ${g.label}`} />
              ))}
            </span>
          ) : 'No checks recorded'}
        />
      </SectionCard>
    );
  })() : null;

  const timeline: TimelineEntry[] = referralMilestones(person, rewards).map((m) => ({
    id: m.id,
    kind: m.kind,
    text: m.text,
    meta: [formatDateTime(m.at), m.by].filter(Boolean).join(' · '),
  }));

  return (
    <EntityWorkspace
      backLabel="Referrals"
      onBack={() => navigate(lastListView('/referrals'))}
      identityRail={identityRail}
      tabs={TABS}
      activeTabId="overview"
      // One section of content, so one tab and nothing to switch. EntityWorkspace requires a tab list.
      onTabChange={() => {}}
      activityRail={<ActivityRail timeline={timeline} />}
      activityOpen={activityOpen}
      onToggleActivity={toggleActivity}
    >
      {referralCard}
      {qualificationCard}
      {rewardCards}
      {evidence}
    </EntityWorkspace>
  );
};

export default ReferralWorkspace;
